/**
 * Keyword tier: pick one `(category, subcategory)` from the eight
 * per-category detectors. Shared by web's `ExpenseCategoryService`
 * and mobile's `runKeywordDetectors` so both apps rank probes, guard
 * against taxonomy drift and treat ties identically.
 */
import type { DetectionResult } from './SubcategoryDetectionService';
import { subcategoryDetectionService } from './SubcategoryDetectionService';
import { getSupportedLanguages, resolveLocaleToLanguage } from './languages';
import type { SupportedLanguage } from './languages';
import {
  KEYWORD_TIE_CONFIDENCE,
  KEYWORD_TIE_MARGIN,
} from './subcategoryDetection';
import { isValidExpensePrediction } from './taxonomy';
import type { ExpenseCategory } from './types';

export interface KeywordPrediction {
  category: ExpenseCategory;
  confidence: number;
  subcategory?: { type: string; confidence: number };
  /** Every category that scored, by raw score descending; the winner first. */
  ranked: ExpenseCategory[];
}

/** Below this the best probe is too noisy to chip; the cloud tier takes over. */
export const KEYWORD_MIN_CATEGORY_CONFIDENCE = 0.5;
/**
 * Floor the form applies before showing a subcategory chip. Keyword
 * answers never fall below it (a category hit is already ≥ 0.5), so
 * the picker doesn't check it.
 */
export const KEYWORD_MIN_SUBCATEGORY_CONFIDENCE = 0.4;

/** What a probe scored before its own in-category tie haircut. */
const rawScore = (result: DetectionResult): number =>
  result.matchScore ?? result.confidence;

/**
 * Rank the detectors' results. Probes whose `(category, subcategory)`
 * pair is not in `EXPENSE_TAXONOMY` are skipped so the next probe can
 * win — the per-language keyword files have drifted (e.g. `pharmacy`
 * under shopping); the `default` sentinel (category hit, no
 * subcategory keyword) is always allowed.
 *
 * Categories compete on their raw match score. Two or more within
 * `KEYWORD_TIE_MARGIN` of the winner is a cross-category tie — real
 * and intentional in the taxonomy (`groceries` in food and shopping,
 * `fitness` in healthcare and entertainment, `supplements` in food and
 * healthcare): "Walmart" alone cannot say which parent the user means,
 * so the winner is reported at `KEYWORD_TIE_CONFIDENCE` and the cloud
 * tier breaks the tie. A clear winner keeps its own confidence, which
 * its detector may already have haircut for a tie between its own
 * subcategories.
 */
export function pickKeywordPrediction(
  detections: ReadonlyMap<string, DetectionResult>
): KeywordPrediction | null {
  let best: { category: ExpenseCategory; result: DetectionResult } | null =
    null;
  let runnerUp = 0;
  const scored: Array<{ category: ExpenseCategory; score: number }> = [];

  for (const [category, result] of detections) {
    if (result.confidence === 0) continue;
    if (
      result.subcategory !== 'default' &&
      !isValidExpensePrediction(category as ExpenseCategory, result.subcategory)
    ) {
      continue;
    }
    const score = rawScore(result);
    scored.push({ category: category as ExpenseCategory, score });
    if (!best || score > rawScore(best.result)) {
      runnerUp = best ? rawScore(best.result) : 0;
      best = { category: category as ExpenseCategory, result };
    } else {
      runnerUp = Math.max(runnerUp, score);
    }
  }

  if (!best || best.result.confidence < KEYWORD_MIN_CATEGORY_CONFIDENCE) {
    return null;
  }

  const tied = rawScore(best.result) - runnerUp <= KEYWORD_TIE_MARGIN;
  const confidence = tied
    ? Math.min(best.result.confidence, KEYWORD_TIE_CONFIDENCE)
    : best.result.confidence;

  const ranked = scored
    .sort((a, b) => b.score - a.score)
    .map(entry => entry.category);

  const { subcategory } = best.result;
  return subcategory === 'default'
    ? { category: best.category, confidence, ranked }
    : {
        category: best.category,
        confidence,
        subcategory: { type: subcategory, confidence },
        ranked,
      };
}

/**
 * Run every detector for `locale` and rank the results. Pure and
 * synchronous. Returns `null` when nothing clears the category floor.
 * Callers own the title-length gate (`MIN_PREDICTION_TITLE_CHARS`).
 */
export function predictExpenseCategoryFromKeywords(
  title: string,
  locale: string | undefined | null
): KeywordPrediction | null {
  return pickKeywordPrediction(
    subcategoryDetectionService.detectAllSubcategories(title, locale)
  );
}

/**
 * Ceiling for a hit found in a language other than the first one asked
 * for. It sits under the scheduler's keyword trust line (0.8), so the
 * cloud tier still confirms a match that only a foreign vocabulary made:
 * the September cross-language collisions ("natural gas" → Portuguese
 * "natural", a sandwich) are the reason the detectors run one language
 * at a time.
 */
export const KEYWORD_FOREIGN_LANGUAGE_CONFIDENCE = 0.75;

/**
 * Run the detectors language by language, in the order given, and take
 * the first hit; the languages the caller did not name run last. The
 * usual order is the app language, then the device language: a Spanish
 * title typed on an English phone still lands, and the region part of a
 * locale plays no role (brand names are shared across languages).
 */
export function predictExpenseCategoryAcrossLanguages(
  title: string,
  preferred: readonly (string | null | undefined)[]
): KeywordPrediction | null {
  const order: SupportedLanguage[] = [];
  for (const locale of preferred) {
    const language = resolveLocaleToLanguage(locale);
    if (language && !order.includes(language)) order.push(language);
  }
  for (const language of getSupportedLanguages()) {
    if (!order.includes(language)) order.push(language);
  }
  for (const [index, language] of order.entries()) {
    const hit = predictExpenseCategoryFromKeywords(title, language);
    if (!hit) continue;
    if (index === 0) return hit;
    const confidence = Math.min(
      hit.confidence,
      KEYWORD_FOREIGN_LANGUAGE_CONFIDENCE
    );
    return {
      ...hit,
      confidence,
      subcategory: hit.subcategory
        ? { ...hit.subcategory, confidence }
        : undefined,
    };
  }
  return null;
}
